import { useMemo } from "react";

import type { SavedToolPermissions } from "../clientTools/approvalModes";

import { useClientToolPreferences } from "./useClientToolPreferences";

const LOADING: SavedToolPermissions = { status: "loading" };
const UNAVAILABLE: SavedToolPermissions = { status: "unavailable" };

/**
 * The person's saved tool permissions, and whether the app has them.
 *
 * `permissions` keeps its identity until what it describes changes, so an
 * effect may depend on it. `reload` requests the saved record again.
 *
 * A record that loaded once stays `loaded` when a later refetch fails, since
 * the query keeps its data.
 */
export function useSavedToolPermissions(): {
  permissions: SavedToolPermissions;
  reload: () => void;
} {
  const { data, fetchStatus, refetch } = useClientToolPreferences();

  // Built from `data` alone, so a background refetch that returns the same
  // record leaves the object as it is.
  const loaded = useMemo<SavedToolPermissions | undefined>(
    () =>
      data === undefined ? undefined : { status: "loaded", preferences: data },
    [data],
  );
  // `fetchStatus`, not `status`: the query's `status` is "pending" both while
  // its first request is in flight and before any request was made, and goes
  // back to "pending" when a refetch starts with no data.
  const notLoaded = fetchStatus === "fetching" ? LOADING : UNAVAILABLE;

  return {
    permissions: loaded ?? notLoaded,
    reload: () => {
      void refetch();
    },
  };
}
